import moment from 'moment';
import { DATE_FORMAT } from 'framework/utils';
import type { ApplicationDetails, ApplicationStageActions, CheckStateKey } from './types';

export type CandidateCheckStatus = 'pending' | 'inProgress' | 'submitted' | 'scheduled' | 'passed' | 'failed';

type CandidateCheckId = 'screening-checklist' | 'screening-interview' | 'role-related-interview' | 'general-interview';

type CheckText = string | ((application: ApplicationDetails) => string);

type CheckActionId =
  | 'confirm-checklist-sent'
  | 'confirm-checklist-received'
  | 'review-completed-checklist'
  | 'schedule-interview'
  | 'provide-interview-results';

type CheckJobAction = {
  id: CheckActionId;
  label: string;
  event: string;
  description?: CheckText;
  onClick: (actions: ApplicationStageActions) => () => void;
};

export type CheckJobState = {
  key: CheckStateKey;
  status: CandidateCheckStatus;
  hint: CheckText;
  action?: CheckJobAction;
};

export type CheckJob = {
  id: CandidateCheckId;
  label: string;
  processTitle: string;
  states: CheckJobState[];
};

export interface CandidateCheck {
  id: CandidateCheckId;
  label: string;
  status: CandidateCheckStatus;
  hint: string;
}

const SCHEDULED_AT_FORMAT = `${DATE_FORMAT.NATURAL} ${DATE_FORMAT.HOURS_MINUTES}`;

const getScheduledInterviewHint = ({ scheduledInterview }: ApplicationDetails) =>
  scheduledInterview
    ? `${moment(scheduledInterview.scheduledAt).format(SCHEDULED_AT_FORMAT)} · ${scheduledInterview.interviewer}`
    : 'Not scheduled';

export const CHECK_JOBS: CheckJob[] = [
  {
    id: 'screening-checklist',
    label: 'Screening checklist',
    processTitle: 'Screening checklist',
    states: [
      {
        key: 'ScreeningChecklistPending',
        status: 'pending',
        hint: 'Not sent',
        action: {
          id: 'confirm-checklist-sent',
          label: 'Confirm checklist is sent',
          event: 'ScreeningChecklistSent',
          description: 'Send screening checklist to the candidate and confirm its delivery',
          onClick: actions => actions.confirmChecklistSent,
        },
      },
      {
        key: 'ScreeningChecklistSent',
        status: 'inProgress',
        hint: 'Waiting for candidate response',
        action: {
          id: 'confirm-checklist-received',
          label: 'Confirm checklist is received',
          event: 'ScreeningChecklistReceived',
          description: 'Receive the completed checklist and confirm it',
          onClick: actions => actions.confirmChecklistReceived,
        },
      },
      {
        key: 'ScreeningChecklistReceived',
        status: 'submitted',
        hint: 'Waiting for review',
        action: {
          id: 'review-completed-checklist',
          label: 'Review completed checklist',
          event: 'ScreeningChecklistReviewed',
          onClick: actions => actions.reviewCompletedChecklist,
        },
      },
      { key: 'ScreeningChecklistPassed', status: 'passed', hint: 'Positive result' },
      { key: 'ScreeningChecklistFailed', status: 'failed', hint: 'Negative result' },
    ],
  },
  {
    id: 'screening-interview',
    label: 'Screening interview',
    processTitle: 'Hold interview',
    states: [
      {
        key: 'ScreeningInterviewPending',
        status: 'pending',
        hint: 'Not scheduled',
        action: {
          id: 'schedule-interview',
          label: 'Schedule interview',
          event: 'ScreeningInterviewScheduled',
          description: 'Generate link for candidate or schedule interview manually',
          onClick: actions => actions.scheduleInterview,
        },
      },
      {
        key: 'ScreeningInterviewScheduled',
        status: 'scheduled',
        hint: getScheduledInterviewHint,
        action: {
          id: 'provide-interview-results',
          label: 'Provide interview results',
          event: 'ScreeningInterviewCompleted',
          description: ({ scheduledInterview }) => scheduledInterview?.type ?? '',
          onClick: actions => actions.provideInterviewResults,
        },
      },
      { key: 'ScreeningInterviewPassed', status: 'passed', hint: 'Positive result' },
      { key: 'ScreeningInterviewFailed', status: 'failed', hint: 'Negative result' },
    ],
  },
  {
    id: 'role-related-interview',
    label: 'Role-related interview',
    processTitle: 'Hold interview',
    states: [
      { key: 'RoleRelatedInterviewPending', status: 'pending', hint: 'Not scheduled' },
      { key: 'RoleRelatedInterviewPassed', status: 'passed', hint: 'Positive result' },
      { key: 'RoleRelatedInterviewFailed', status: 'failed', hint: 'Negative result' },
    ],
  },
  {
    id: 'general-interview',
    label: 'General interview',
    processTitle: 'Hold interview',
    states: [
      { key: 'GeneralInterviewPending', status: 'pending', hint: 'Not scheduled' },
      { key: 'GeneralInterviewPassed', status: 'passed', hint: 'Positive result' },
      { key: 'GeneralInterviewFailed', status: 'failed', hint: 'Negative result' },
    ],
  },
];

export const resolveCheckText = (text: CheckText, application: ApplicationDetails): string =>
  typeof text === 'function' ? text(application) : text;

type CheckedApplication = Pick<ApplicationDetails, 'jobsStates'>;

export const getCandidateCheckStates = (application: CheckedApplication): { job: CheckJob; state: CheckJobState }[] =>
  CHECK_JOBS.map(job => ({
    job,
    state: job.states.find(state => application.jobsStates.includes(state.key)) ?? job.states[0],
  }));

export const getCandidateChecks = (application: ApplicationDetails): CandidateCheck[] =>
  getCandidateCheckStates(application).map(({ job, state }) => ({
    id: job.id,
    label: job.label,
    status: state.status,
    hint: resolveCheckText(state.hint, application),
  }));

export const isEveryCheckPassed = (application: CheckedApplication): boolean =>
  getCandidateCheckStates(application).every(({ state }) => state.status === 'passed');
